import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useBlocker } from '@tanstack/react-router';
import { Button, Dialog } from '@givmo/design-system';

interface UnsavedChangesState {
  register: (id: symbol, dirty: boolean) => void;
  unregister: (id: symbol) => void;
  hasUnsavedChanges: boolean;
  confirmDiscard: () => Promise<boolean>;
  /**
   * The person has already left on purpose, and the page is about to go: "Sign in again" has signed
   * them out and now reloads (AS-PWL-2e). From here neither the dialog nor the browser's own "Leave
   * site?" asks, since no answer could keep an edit the sign-out has already ended. Takes effect at
   * once, with no render in between. Never in place of asking BEFORE an act that would discard edits.
   */
  leaveWithoutAsking: () => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesState | null>(null);

interface PendingConfirmation {
  resolve: (discard: boolean) => void;
}

/** Owns the single router blocker, before-unload guard, and design-system confirmation dialog. */
export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const [dirtyIds, setDirtyIds] = useState<ReadonlySet<symbol>>(() => new Set());
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);
  const hasUnsavedChanges = dirtyIds.size > 0;
  // A leave the person already chose (`leaveWithoutAsking`). The history asks both functions at the
  // moment of leaving, so setting it is enough: no render has to land before the page goes.
  const leavingRef = useRef(false);
  const stillAsking = useCallback(() => !leavingRef.current, []);

  const blocker = useBlocker({
    shouldBlockFn: stillAsking,
    disabled: !hasUnsavedChanges,
    enableBeforeUnload: stillAsking,
    withResolver: true,
  });

  const register = useCallback((id: symbol, dirty: boolean) => {
    setDirtyIds((current) => {
      if (current.has(id) === dirty) return current;
      const next = new Set(current);
      if (dirty) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const unregister = useCallback((id: symbol) => {
    setDirtyIds((current) => {
      if (!current.has(id)) return current;
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  }, []);

  const confirmDiscard = useCallback((): Promise<boolean> => {
    if (!hasUnsavedChanges) return Promise.resolve(true);
    return new Promise<boolean>((resolve) => setPendingConfirmation({ resolve }));
  }, [hasUnsavedChanges]);

  const leaveWithoutAsking = useCallback(() => {
    leavingRef.current = true;
  }, []);

  const value = useMemo(
    () => ({ register, unregister, hasUnsavedChanges, confirmDiscard, leaveWithoutAsking }),
    [register, unregister, hasUnsavedChanges, confirmDiscard, leaveWithoutAsking],
  );

  const navigationBlocked = blocker.status === 'blocked';
  const open = navigationBlocked || pendingConfirmation !== null;

  const keepEditing = () => {
    if (navigationBlocked) blocker.reset();
    pendingConfirmation?.resolve(false);
    setPendingConfirmation(null);
  };

  const discard = () => {
    if (navigationBlocked) blocker.proceed();
    pendingConfirmation?.resolve(true);
    setPendingConfirmation(null);
  };

  return (
    <UnsavedChangesContext value={value}>
      {children}
      <Dialog.Root open={open} onOpenChange={(next) => !next && keepEditing()}>
        <Dialog.Content aria-label="Unsaved changes" testId="unsaved-changes-dialog">
          <div className="flex flex-col gap-16">
            <div className="flex flex-col gap-4">
              <Dialog.Title className="font-w700 text-h6 text-text-strong">
                Discard unsaved changes?
              </Dialog.Title>
              <Dialog.Description className="text-s14 text-text-secondary">
                Your edits have not been saved. Leaving now will discard them.
              </Dialog.Description>
            </div>
            <div className="flex items-center justify-end gap-8">
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={keepEditing}
                testId="unsaved-changes-keep"
              >
                Keep editing
              </Button>
              <Button
                appearance="saas"
                tone="danger"
                size="sm"
                onClick={discard}
                testId="unsaved-changes-discard"
              >
                Discard changes
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Root>
    </UnsavedChangesContext>
  );
}

/** Register one form's dirty state. Standalone preview/test renders safely no-op without the provider. */
export function useUnsavedChanges(dirty: boolean): void {
  const context = use(UnsavedChangesContext);
  const id = useRef(Symbol('unsaved-form'));
  const register = context?.register;
  const unregister = context?.unregister;

  useEffect(() => {
    register?.(id.current, dirty);
    return () => unregister?.(id.current);
  }, [dirty, register, unregister]);
}

type UnsavedChangesGuard = Pick<
  UnsavedChangesState,
  'hasUnsavedChanges' | 'confirmDiscard' | 'leaveWithoutAsking'
>;

/** Without a provider nothing is registered, so nothing asks. One object, so a caller's memo holds. */
const NO_GUARD: UnsavedChangesGuard = {
  hasUnsavedChanges: false,
  confirmDiscard: () => Promise.resolve(true),
  leaveWithoutAsking: () => {},
};

/**
 * Optional consumer, used by the org switcher and by the console's step-up host ("Sign in again");
 * no provider means there is nothing registered.
 */
export function useUnsavedChangesGuard(): UnsavedChangesGuard {
  return use(UnsavedChangesContext) ?? NO_GUARD;
}
